import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import HtmlBodyField from "./HtmlBodyField";
import ScheduleBuilder from "./ScheduleBuilder";
import { buildPayload } from "./automationConfigs";
import EmailPreview from "../../Components/LaunchedTemplates/EmailPreview";
import {
  fetchEventMappings,
  fetchCronJobMappings,
} from "./scheduledAutomationsApi";
import "./AutomationFormModal.scss";

const AutomationFormModal = ({
  config,
  initialData,
  notificationTypeOptions = [],
  typeOptions = [],
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [emailMappings, setEmailMappings] = useState({
    fromEmailSources: [],
    toEmailSources: [],
  });
  const [activeField, setActiveField] = useState(null);
  const [errors, setErrors] = useState({});
  const fieldRefs = useRef({});
  const emailEditorRef = useRef(null);
  const isEdit = Boolean(initialData[config.idField]);
  const selectedTypeId = form[config.typeIdField];
  const selectedType = typeOptions.find(
    (option) =>
      Number(option[config.typeOption.idField]) === Number(selectedTypeId),
  );

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setTemplateField = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [config.templateField]: { ...prev[config.templateField], [field]: value },
    }));

  useEffect(() => {
    const typeId = selectedTypeId;
    if (!typeId) {
      setPlaceholders([]);
      setEmailMappings({ fromEmailSources: [], toEmailSources: [] });
      return;
    }
    let cancelled = false;
    setLoadingPlaceholders(true);
    const request = config.hasCronFields
      ? fetchCronJobMappings(typeId)
      : fetchEventMappings(typeId);
    request
      .then((res) => {
        if (cancelled) return;
        const mappings = res.data?.data || {};
        setPlaceholders(mappings.placeHolders || []);
        setEmailMappings({
          fromEmailSources: mappings.fromEmailSources || [],
          toEmailSources: mappings.toEmailSources || [],
        });
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load placeholders.");
      })
      .finally(() => {
        if (!cancelled) setLoadingPlaceholders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [config, selectedTypeId]);

  const fromEmailOptions = emailMappings.fromEmailSources;
  const toEmailOptions = emailMappings.toEmailSources;

  const insertPlaceholder = (placeholder) => {
    const token = placeholder.startsWith("{{")
      ? placeholder
      : `{{${placeholder}}}`;
    const field = activeField || "body";
    if (field === "body" && emailEditorRef.current?.getEditor) {
      const editor = emailEditorRef.current.getEditor();
      const range = editor.getSelection(true);
      editor.insertText(range?.index ?? editor.getLength(), token);
      editor.setSelection((range?.index ?? editor.getLength()) + token.length);
      handleBodyChange(editor.root.innerHTML);
      return;
    }
    const ref =
      field === "body" ? fieldRefs.current.body : fieldRefs.current[field];
    const value = template[field] || "";
    const start = ref?.selectionStart ?? value.length;
    const end = ref?.selectionEnd ?? start;
    setTemplateField(
      field,
      `${value.slice(0, start)}${token}${value.slice(end)}`,
    );
    requestAnimationFrame(() => {
      ref?.focus();
      ref?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  // The body is always treated as HTML (per the backend contract where
  // { body, htmlBody } together describe how body should be interpreted),
  // so htmlBody is pinned to true here.
  const handleBodyChange = (html) =>
    setForm((prev) => ({
      ...prev,
      [config.templateField]: {
        ...prev[config.templateField],
        body: html,
        htmlBody: true,
      },
    }));

  const toggleNotificationType = (notificationTypeId) =>
    setForm((prev) => {
      const current = prev.notificationTypeIds || [];
      const next = current.includes(notificationTypeId)
        ? current.filter((id) => id !== notificationTypeId)
        : [...current, notificationTypeId];
      return { ...prev, notificationTypeIds: next };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!form.taskName.trim()) {
      nextErrors.taskName = "Task name is required.";
    }
    if (!form[config.typeIdField]) {
      nextErrors.type = `${
        config.hasCronFields ? "Cron job type" : "Event type"
      } is required.`;
    }
    if (!form.notificationTypeIds?.length) {
      nextErrors.notificationTypes = "Select at least one notification type.";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    const payload = buildPayload(config, form);

    setSaving(true);
    try {
      if (isEdit) {
        await config.api.update(payload);
        toast.success(`${config.label} updated.`);
      } else {
        await config.api.create(payload);
        toast.success(`${config.label} created.`);
      }
      onSaved();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Couldn't save this ${config.label.toLowerCase()}.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const template = form[config.templateField] || {};
  const selectedNotificationTypes = (form.notificationTypeIds || [])
    .map((id) =>
      notificationTypeOptions.find(
        (option) => option.notificationTypeId === Number(id),
      ),
    )
    .filter(Boolean);
  const hasEmailNotification = selectedNotificationTypes.some((type) =>
    /EMAIL/i.test(`${type.type || ""} ${type.name || ""}`),
  );
  const hasInAppNotification = selectedNotificationTypes.some((type) =>
    /IN[_\s-]?APP/i.test(`${type.type || ""} ${type.name || ""}`),
  );
  const showEmailFields =
    selectedNotificationTypes.length === 0 || hasEmailNotification;
  const showInAppFields =
    selectedNotificationTypes.length === 0 || hasInAppNotification;
  const hasInAppPreview =
    showInAppFields &&
    (template.notificationHeading || template.notificationMessage);
  // notificationHeading/notificationMessage are plain <textarea> fields with
  // no explicit htmlBody flag (unlike the email body, which is always
  // treated as HTML) — people sometimes paste markup into them anyway, so
  // detect it heuristically rather than always treating the value as plain
  // text.
  const looksLikeHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(value || "");

  return (
    <div className="automation-modal__backdrop" onClick={onClose}>
      <div
        className="automation-modal automation-modal--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="automation-modal__header">
          <div>
            <h2>{isEdit ? `Edit ${config.label}` : `New ${config.label}`}</h2>
            <p className="automation-modal__header-subtitle">
              {selectedType
                ? `${config.hasCronFields ? "Cron job type" : "Event type"}: ${
                    selectedType[config.typeOption.labelField]
                  }`
                : "Configure delivery, scheduling, and template content."}
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon icon="mdi:close" width="20" />
          </button>
        </header>

        <form className="automation-modal__layout" onSubmit={handleSubmit}>
          <div className="automation-modal__fields">
            <section className="form-section">
              <h3>Details</h3>
              <div className="form-grid">
                <label className="form-field">
                  <span>Task name <b className="required-mark">*</b></span>
                  <input
                    className={errors.taskName ? "input-error" : ""}
                    type="text"
                    value={form.taskName}
                    onChange={(e) => setField("taskName", e.target.value)}
                    required
                  />
                  {errors.taskName && <small className="field-error-text">{errors.taskName}</small>}
                </label>

                <label className="form-field form-field--full">
                  <span>Description</span>
                  <textarea
                    rows={2}
                    value={form.description || ""}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </label>

                <div className="form-field form-field--full">
                  <span>Notification types <b className="required-mark">*</b></span>
                  {notificationTypeOptions.length === 0 ? (
                    <small>
                      No notification types loaded — check
                      /admin/notification-types/all.
                    </small>
                  ) : (
                    <div className="checkbox-list">
                      {notificationTypeOptions.map((nt) => (
                        <label
                          key={nt.notificationTypeId}
                          className="checkbox-list__item"
                        >
                          <input
                            type="checkbox"
                            checked={(form.notificationTypeIds || []).includes(
                              nt.notificationTypeId,
                            )}
                            onChange={() =>
                              toggleNotificationType(nt.notificationTypeId)
                            }
                          />
                          <span>
                            {nt.name} <small>({nt.type})</small>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {errors.notificationTypes && (
                    <small className="field-error-text">{errors.notificationTypes}</small>
                  )}
                </div>

              </div>
            </section>

            {config.hasCronFields && (
              <section className="form-section">
                <h3>Schedule</h3>
                <ScheduleBuilder
                  value={form.cronJobExpression}
                  onChange={(expr) => setField("cronJobExpression", expr)}
                />
                <small className="form-note">
                  Builds the cron expression sent as{" "}
                  <code>cronJobExpression</code>. Kept <code>triggerDate</code>{" "}
                  / <code>days</code> below as advanced fields since your schema
                  sends both — confirm with the backend whether they're still
                  needed alongside the cron expression, or leftover from an
                  earlier design.
                </small>
                <details className="advanced-fields">
                  <summary>Advanced: triggerDate / days</summary>
                  <div className="form-grid">
                    <label className="form-field">
                      <span>Trigger date</span>
                      <select
                        value={form.triggerDate}
                        onChange={(e) =>
                          setField("triggerDate", e.target.value)
                        }
                      >
                        <option value="OnDATE">OnDATE</option>
                        <option value="DateBEFORE">DateBEFORE</option>
                        <option value="DateAFTER">DateAFTER</option>
                      </select>
                    </label>
                    <label className="form-field">
                      <span>Days</span>
                      <input
                        type="number"
                        value={form.days}
                        onChange={(e) => setField("days", e.target.value)}
                      />
                    </label>
                  </div>
                </details>
              </section>
            )}

            <section className="form-section">
              <h3>Recipients</h3>
              <div className="form-grid">
                <label className="form-field">
                  <span>Send to</span>
                  <select
                    value={form.toEmailSourceFrom}
                    onChange={(e) =>
                      setField("toEmailSourceFrom", e.target.value)
                    }
                  >
                    <option value="">Select…</option>
                    {toEmailOptions.map((opt) => (
                      <option
                        key={opt.toEmailId}
                        value={opt.toEmailSourceCode}
                      >
                        {opt.toEmailSourceName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-field">
                  <span>Send from</span>
                  <select
                    value={form.fromEmailSource}
                    onChange={(e) =>
                      setField("fromEmailSource", e.target.value)
                    }
                  >
                    <option value="">Select…</option>
                    {fromEmailOptions.map((opt) => (
                      <option
                        key={opt.fromEmailId}
                        value={opt.fromEmailSourceCode}
                      >
                        {opt.fromEmailSourceName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            <fieldset className="template-fieldset">
              <legend>Template</legend>
              <section className="form-section">
              <h3>Template</h3>
              <div className="form-grid">
                <label className="form-field">
                  <span>Template name</span>
                  <input
                    type="text"
                    value={template.templateName}
                    onChange={(e) =>
                      setTemplateField("templateName", e.target.value)
                    }
                  />
                </label>
                {showEmailFields && (
                  <label className="form-field">
                    <span>Subject</span>
                    <input
                      type="text"
                      value={template.subject}
                      ref={(el) => (fieldRefs.current.subject = el)}
                      onFocus={() => setActiveField("subject")}
                      onChange={(e) =>
                        setTemplateField("subject", e.target.value)
                      }
                    />
                  </label>
                )}
                {showInAppFields && (
                  <label className="form-field">
                    <span>Notification heading</span>
                    <input
                      type="text"
                      value={template.notificationHeading}
                      ref={(el) => (fieldRefs.current.notificationHeading = el)}
                      onFocus={() => setActiveField("notificationHeading")}
                      onChange={(e) =>
                        setTemplateField("notificationHeading", e.target.value)
                      }
                    />
                  </label>
                )}
              </div>

              {showEmailFields && (
                <div className="form-field form-field--full">
                  <span>Email body</span>
                  <HtmlBodyField
                    value={template.body}
                    onChange={handleBodyChange}
                    onEditorReady={(instance) => {
                      emailEditorRef.current = instance;
                    }}
                    onSourceReady={(element) => {
                      fieldRefs.current.body = element;
                    }}
                    onFocus={() => setActiveField("body")}
                  />
                </div>
              )}

              {showInAppFields && (
                <label className="form-field form-field--full">
                  <span>In-app notification message</span>
                  <textarea
                    rows={3}
                    value={template.notificationMessage}
                    ref={(el) => (fieldRefs.current.notificationMessage = el)}
                    onFocus={() => setActiveField("notificationMessage")}
                    onChange={(e) =>
                      setTemplateField("notificationMessage", e.target.value)
                    }
                  />
                </label>
              )}

              <div className="placeholder-reference form-field--full">
                <span className="placeholder-reference__title">Available placeholders</span>
                {loadingPlaceholders ? (
                  <small>Loading placeholders…</small>
                ) : placeholders.length ? (
                  <div className="placeholder-reference__list">
                    {placeholders.map((item) => (
                      <button
                        type="button"
                        className="placeholder-chip"
                        key={item.placeHolderId}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertPlaceholder(item.placeHolder)}
                      >
                        {item.placeHolder}
                        {item.description && (
                          <span className="placeholder-chip__tooltip">
                            {item.description}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <small>Select an event type to load placeholders.</small>
                )}
              </div>
              </section>
            </fieldset>

            <footer className="automation-modal__footer">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={saving}
              >
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
              </button>
            </footer>
          </div>

          <aside className="automation-modal__preview">
            <div className="automation-modal__preview-header">
              <span>Live preview</span>
              <p>Updates as you edit the form</p>
            </div>

            {showEmailFields && template.subject && (
              <p className="automation-modal__preview-subject">
                {template.subject}
              </p>
            )}

            {showEmailFields && (
              <>
                <EmailPreview
                  body={template.body}
                  htmlBody={template.htmlBody}
                  label="Email template"
                  variant="editor"
                />
                {!template.body && (
                  <div className="automation-modal__preview-empty">
                    Start typing a body to see the email here.
                  </div>
                )}
              </>
            )}

            {hasInAppPreview && (
              <div className="automation-modal__preview-inapp">
                <span className="automation-modal__preview-label">
                  In-app notification
                </span>
                {template.notificationHeading &&
                  (looksLikeHtml(template.notificationHeading) ? (
                    <EmailPreview
                      body={template.notificationHeading}
                      htmlBody
                      label="Notification heading"
                      variant="inapp"
                    />
                  ) : (
                    <h4>{template.notificationHeading}</h4>
                  ))}
                {template.notificationMessage &&
                  (looksLikeHtml(template.notificationMessage) ? (
                    <EmailPreview
                      body={template.notificationMessage}
                      htmlBody
                      label="Notification message"
                      variant="inapp"
                    />
                  ) : (
                    <p>{template.notificationMessage}</p>
                  ))}
              </div>
            )}
          </aside>
        </form>
      </div>
    </div>
  );
};

export default AutomationFormModal;
